import { Button } from '@ooh/ui/components/button';
import type { QuickAddOfferDraftOption } from './quick-add-offer-picker';
import { SelectedOfferCard } from './quick-add-offer-picker-sections';

export interface QuickAddOfferPickerCompactTriggerProps {
  drafts: QuickAddOfferDraftOption[];
  selectedDraftId: string | null;
  draftUnitLabel: string;
  draftSummarySeparator: string;
  changeSelectionLabel: string;
  pickOfferLabel: string;
  onOpenPicker: () => void;
}

/**
 * Compact "which offer am I adding to" affordance for `UnifiedSelectPopover`'s embedded
 * offer-picker slot — replaces rendering the full `QuickAddOfferPicker` inline (which used
 * to cram its own search box, draft list, and "Anuluj zmianę" step into the popover). Shows
 * the already-selected draft as a summary card with a "Zmień" action, or a plain "pick an
 * offer" trigger when nothing is selected yet — both open the SAME full-size
 * `QuickAddOfferPicker` (`presentation="modal"`, `initialMode="changing"`) the caller already
 * renders for the header "Dodaj do oferty" flow, instead of a second, cramped copy of it.
 */
export function QuickAddOfferPickerCompactTrigger({
  drafts,
  selectedDraftId,
  draftUnitLabel,
  draftSummarySeparator,
  changeSelectionLabel,
  pickOfferLabel,
  onOpenPicker,
}: QuickAddOfferPickerCompactTriggerProps) {
  const selectedDraft =
    drafts.find((draft) => draft.id === selectedDraftId && draft.editable) ?? null;

  if (selectedDraft) {
    return (
      <SelectedOfferCard
        draft={selectedDraft}
        draftUnitLabel={draftUnitLabel}
        draftSummarySeparator={draftSummarySeparator}
        pendingUnitsLabel=""
        changeSelectionLabel={changeSelectionLabel}
        onChange={onOpenPicker}
      />
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      fullWidth
      onClick={onOpenPicker}
      data-testid="quick-add-offer-picker-trigger"
    >
      {pickOfferLabel}
    </Button>
  );
}
